import { IProduct } from '../../models/product.model';
import { Baseline, IElement } from '../../models/project.model';
import {
  isProductCategory,
  isSystemCategory,
} from '../element-category.helpers';
import { getElementCategoryId, getElementName } from '../element.helpers';
import { ElementCategoryID } from '../../models/element-categories.model';
import { getWallThermalResistance } from './wall-energy.helpers';
import {
  getAreaUnit,
  getElementArea,
  getElementFillGrade,
  getElementQuantityResolvedCountByName,
  getThicknessUnit,
} from '../element-quantity.helpers';
import {
  getElementLayerGroups,
  getMaxThickness,
} from '../layer-preview.helpers';
import { genericProductsLookup } from '../../generic-products';
import { getProduct } from '../product.helpers';
import {
  getChildElements,
  isElement,
  isProductElement,
} from '../recursive-element.helpers';
import { makeSentence } from '../string.helpers';
import { validateFiniteNumber } from '../../validation/number.validation';
import { isDefined } from '../array.helpers';
import { sum } from '../math.helpers';
import { getElementMass } from '../results.helpers';
import {
  getSlabOnGroundThermalResistance,
  getRoofThermalResistance,
} from './slab-energy.helpers';
import {
  IThermalLayer,
  IThermalProduct,
  BUILDING_ENVELOPE_TYPE,
  THERMAL_BRIDGES_LOSS_FACTOR,
} from './energy.models';
import {
  getBuildingEnvelopeElements,
  getBuildingEnvelopeType,
} from './building-envelope.helpers';
import { isSingleFamilyHouse } from './energy.helpers';
import { ActivityEnum, ActivityMix } from '../../models/activities.model';
import { ElementPropertyName } from '../../models/element-property.model';
import { getElementPropertyResolvedCountByNameOrId } from '../element-property.helpers';
import { ThicknessUnit } from '../../models/element-quantities.model';
import { IProposal } from '@shared/models/proposals.model';

interface UValueAndArea {
  u: number;
  area: number;
}

/**
 * Get average thermal transmittance Um (W/m²K) from a list of U-values and areas
 * Från https://www.daloc.se/ditt-byggprojekt/regler-och-klassningar/u-varde/
 * Så här räknar du ut en byggnads genomsnittliga värmegenomgångskoefficient (Um)
 * 1. Ta U-värdet för varje separat byggdel och multiplicera med byggdelens area.
 * 2. Sen summerar du värdet för byggdelarna tillsammans med summan av byggnadens köldbryggor.
 * 3. Slutligen delar du den framräknade summan med byggnadens omslutande area (Aom). Enheten för Um är W/m2K.
 * @param uValues
 * @returns The average thermal transmittance Um (W/m²K) including thermal bridges template factor
 */
export const getAverageThermalTransmittance = (
  thermalBridgesLossFactor: number = THERMAL_BRIDGES_LOSS_FACTOR,
  ...uValues: UValueAndArea[]
) => {
  const totalArea = sum(uValues, 'area');
  const uValueArea = sum(uValues, (u) => u.u * u.area);
  if (!totalArea || !uValueArea) {
    return 0;
  }
  return (uValueArea / totalArea) * (1 + thermalBridgesLossFactor);
};

/**
 * Get average thermal transmittance Um (W/m²K) from a building envelope
 * @param version
 * @returns
 */
export const getAverageThermalTransmittanceFromBuildingEnvelope = (
  version: Baseline,
): number => {
  const buildingEnvelopeElements = getBuildingEnvelopeElements(version);
  const uValues = buildingEnvelopeElements
    .map((e) => elementToUValueAndArea(e))
    .filter(isDefined);
  return getAverageThermalTransmittance(undefined, ...uValues);
};

/**
 * Get building transmission energy losses per hour per degree difference in-/outside temperature (kW per degree) from a building envelope
 * @param version
 * @param thermalBridgesLossFactor Thermal bridges loss factor (köldbryggor)
 * @param proposal When provided, only envelope elements active in this proposal are included (for per-proposal heat demand).
 * @returns Building transmission losses in W per degree (Kelvin)
 */
export const getTransmissionLossInWPerDegree = (
  version: Baseline,
  thermalBridgesLossFactor: number = THERMAL_BRIDGES_LOSS_FACTOR,
  proposal?: IProposal,
): number => {
  const buildingEnvelopeElements = getBuildingEnvelopeElements(
    version,
    proposal,
  );
  const uValues = getUValuesAndAreasFromElements(
    buildingEnvelopeElements,
  ).filter(({ u }) => u > 0); // Filter out windows that have no thermal transmittance
  const buildingEnvelopeArea = sum(uValues, 'area');
  const uValue = getAverageThermalTransmittance(
    thermalBridgesLossFactor,
    ...uValues,
  );

  // W/m²K * m² = W/K
  return uValue * buildingEnvelopeArea;
};

/**
 * Get all building envelope elements.
 * Currently elements with a thermal transmittance property.
 * @param buildingEnvelopeElements
 * @returns
 */
export const getBuildingEnvelopeArea = (
  buildingEnvelopeElements: IElement[],
): number => {
  const uValues = getUValuesAndAreasFromElements(buildingEnvelopeElements);
  return sum(uValues, 'area');
};

/**
 * Get U-values and areas from a list of elements
 * @param elements
 * @returns
 */
const getUValuesAndAreasFromElements = (
  elements: IElement[],
): UValueAndArea[] =>
  elements.map((e) => elementToUValueAndArea(e)).filter(isDefined);

const elementToUValueAndArea = (
  element: IElement,
): UValueAndArea | undefined => {
  const u = getElementPropertyResolvedCountByNameOrId(
    element,
    ElementPropertyName.ThermalTransmittance,
    0,
  );
  const area = getElementArea(element);
  if (!u || !area) {
    return undefined;
  }
  return { u, area };
};

/**
 * Get thermal transmittance U (W/m²K) from an element
 * @param element
 * @param products
 * @returns
 */
export const getElementThermalTransmittance = (
  version: Baseline,
  element: IElement,
): number => {
  // TODO: Implement special extra calculations for foundations
  return getGenericThermalTransmittanceFromElement(version, element) ?? 0;
};

/**
 * Get thermal resistance R (m²K/W) from an element
 * @param element
 * @returns
 */
const getElementResistance = (element: IElement) => {
  const categoryId = getElementCategoryId(element);

  if (categoryId === ElementCategoryID.Wall) {
    return getWallThermalResistance();
  }
  if (categoryId === ElementCategoryID.Slab) {
    const buildingEnvelopeType = getBuildingEnvelopeType(element);
    if (buildingEnvelopeType === BUILDING_ENVELOPE_TYPE.WALL) {
      return getSlabOnGroundThermalResistance();
    }
    if (buildingEnvelopeType === BUILDING_ENVELOPE_TYPE.ROOF) {
      return getRoofThermalResistance();
    }
  }

  return 0;
};

export const elementToThermalLayers = (
  version: Baseline,
  element: IElement,
): IThermalLayer[] => {
  const categoryId = getElementCategoryId(element);

  // Only calculate for system categories like Wall, Roof, Floor, etc.
  if (!isSystemCategory(categoryId)) {
    return [];
  }
  const resistance = getElementResistance(element);
  const childElements = getChildElements(element).filter(isElement);
  const groups = getElementLayerGroups(childElements);
  const thicknessUnit = getThicknessUnit(element);
  const areaUnit = getAreaUnit(element);
  const area = getElementQuantityResolvedCountByName(element, areaUnit, 0);

  const layers = groups
    .map((group) => getLayer(version, group, thicknessUnit, area, resistance))
    .filter(isDefined);

  return applyHeatEnergyPerAreaToLayers(layers);
};

/**
 * Get thermal transmittance U (W/m²K) from an element assuming layer structure
 * @param element
 * @param products
 * @returns
 */
export const getGenericThermalTransmittanceFromElement = (
  version: Baseline,
  element: IElement,
): number => {
  if (!isSystemCategory(getElementCategoryId(element))) {
    return 0;
  }

  const layers = elementToThermalLayers(version, element);
  return getThermalTransmittanceFromLayers(
    layers,
    getElementResistance(element),
  );
};

const getTotalResistanceFromLayers = (layers: IThermalLayer[]): number =>
  sum(layers, (layer) => transmittanceToResistance(layer.thermalTransmittance));

const transmittanceToResistance = (transmittance: number): number =>
  transmittance ? 1 / transmittance : 0;

const applyHeatEnergyPerAreaToLayers = (
  layers: IThermalLayer[],
): IThermalLayer[] => {
  const Rtot = getTotalResistanceFromLayers(layers);
  const Utot = 1 / Rtot;

  let currentTemp = 0;

  return layers.map((layer) => {
    const tempDiff = Utot / layer.thermalTransmittance; // Så mkt faller temp genom detta lager
    const heatEnergyPerArea =
      layer.massPerArea * layer.heatCapacity * (currentTemp + tempDiff / 2); // så mkt värmeenergi håller detta lager vid Tinne = Tute+1
    currentTemp += tempDiff;
    return { ...layer, heatEnergyPerArea };
  });
};

/**
 * Get thermal resistance R (m²K/W) from an element
 * @param conductivity Thermal conductivity (W/mK)
 * @param thickness Thickness (m)
 * @returns
 */
export const conductivityToResistance = (
  conductivity: number,
  thickness: number,
): number => {
  return thickness / conductivity;
};

export const getLayer = (
  version: Baseline,
  group: IElement[],
  thicknessUnit: ThicknessUnit,
  area: number,
  resistance: number,
): IThermalLayer | undefined => {
  // During enrichment all products might not be available so include generic products lookup to make it slightly more robust
  const productLookup = {
    ...genericProductsLookup,
    ...version.products,
  };
  const thickness = getMaxThickness(thicknessUnit, ...group);
  const elements = group.filter((e) =>
    isProductCategory(getElementCategoryId(e)),
  );

  if (elements.length === 0 || !thickness || !resistance) {
    return undefined;
  }

  const products = elements.reduce((acc, element) => {
    const mass = getElementMass(version, element);
    const share = getElementShare(group, element, thicknessUnit);
    const productElements = getChildElements(element)
      .filter(isProductElement)
      .filter((p) => p.count.resolved);

    const totalCount = sum(productElements.map((e) => e.count.resolved));

    if (!totalCount || !productElements.length) {
      return acc;
    }

    const products: IThermalProduct[] = productElements.flatMap((el) => {
      const p = getProduct(productLookup, el, false); // Before enrichment all products might not be available
      if (!p) return [];
      return [
        {
          ...p,
          share: share * (el.count.resolved / totalCount),
          mass,
        },
      ];
    });

    return [...acc, ...products];
  }, [] as IThermalProduct[]);

  const name = makeSentence(
    ...elements.map((e) => getElementName(e, productLookup)),
  );

  const mass = sum(products, 'mass');
  const massPerArea = area ? mass / area : 0;
  const heatCapacity = sum(
    products,
    (p) => (p.characteristics?.heatCapacity ?? 0) * p.share,
  );

  if (!products.length) {
    return undefined;
  }

  const base = {
    name,
    thickness,
    products,
  };

  const thermalTransmittance = getThermalTransmittanceFromLayers(
    [base],
    resistance,
  );

  return {
    ...base,
    thermalTransmittance,
    massPerArea,
    area,
    heatCapacity,
    heatEnergyPerArea: 0,
  };
};

export const getElementShare = (
  group: IElement[],
  element: IElement,
  thicknessUnit: ThicknessUnit,
) => {
  const totalThickness = getMaxThickness(thicknessUnit, ...group);
  const thickness = getMaxThickness(thicknessUnit, element);
  const fillGrade = getElementFillGrade(element);
  return (fillGrade * thickness) / totalThickness;
};

const getUMethodThermalTransmittance = (
  layers: Pick<IThermalLayer, 'thickness' | 'products'>[],
  resistance: number,
): number => {
  const stepper: number[] = [];

  const step = (i: number): boolean => {
    const layer = layers[i];

    if (stepper[i] && layer && stepper[i] < layer.products.length - 1) {
      stepper[i]++;
      return true;
    } else {
      if (i + 1 >= stepper.length) return false; // no more combinations
      stepper[i] = 0;
      return step(i + 1);
    }
  };

  while (stepper.length < layers.length) {
    stepper.push(0);
  }

  let U_UMethod: number = 0;
  do {
    let R: number = resistance;
    let surfaceShare: number = 1;
    for (let i = 0; i < layers.length; i++) {
      const current = stepper[i];
      const layer = layers[i];

      const product =
        current === undefined ? undefined : layer?.products[current];

      const lambda = product
        ? getProductThermalConductivity(product)
        : undefined;

      if (product) {
        surfaceShare *= product.share;
      }
      R += lambda && layer ? layer.thickness / lambda : 0;
    }
    U_UMethod += (1 / R) * surfaceShare;
  } while (step(0));
  return U_UMethod;
};

// lambdavärdesmetod λ-värde Värmeledningsförmåga, värmekonduktivitet (W/mK, W/m°C)
const getLambdaThermalTransmittance = (
  layers: Pick<IThermalLayer, 'thickness' | 'products'>[],
  resistance: number,
): number => {
  let R_lambda: number = resistance;
  for (const element of layers) {
    let lambdaMean: number = 0;
    for (const product of element.products) {
      lambdaMean += product.share * getProductThermalConductivity(product);
    }
    R_lambda += element.thickness / lambdaMean;
  }
  // U = 1 / R
  return 1 / R_lambda;
};

/**
 * Get thermal transmittance U (W/m²K)
 * @param layers
 * @returns
 */
const getThermalTransmittanceFromLayers = (
  layers: Pick<IThermalLayer, 'thickness' | 'products'>[],
  resistance: number,
): number => {
  if (layers.length === 0) {
    return 0;
  }
  const U_lambda: number = getLambdaThermalTransmittance(layers, resistance);
  const U_UMethod: number = getUMethodThermalTransmittance(layers, resistance);
  return validateFiniteNumber(
    (2 * U_lambda * U_UMethod) / (U_lambda + U_UMethod),
  );
};

const getProductThermalConductivity = (product: IProduct): number => {
  return product.characteristics?.thermalConductivity ?? 0;
};

/**
 * Get the max allowed average thermal transmittance (W/m2K) based on the activity mix and the heated area.
 * Genomsnittlig värmegenomgångskoefficient (Um) [W/m2K]
 * Page 16: https://rinfo.boverket.se/BFS2011-6/pdf/BFS2024-14.pdf
 * https://www.boverket.se/sv/energideklaration/energideklaration/energideklarationens-innehall/
 * @param heatedArea - The heated area
 * @param activityMix - The activity mix
 * @returns The max allowed average thermal transmittance
 */
export const getMaxAllowedAverageThermalTransmittance = (
  heatedArea: number,
  activityMix?: ActivityMix,
): number => {
  if (heatedArea <= 50) {
    return 0.33;
  } else if (isSingleFamilyHouse(heatedArea)) {
    return 0.3;
  } else {
    // TODO: Support office as an activity
    const office = 0; // activityMix[ActivityEnum.Office] ?? 0;
    const apartment = activityMix?.[ActivityEnum.Apartments] ?? 1 - office;
    return (office * 0.5 + apartment * 0.4) / (office + apartment);
  }
};
